import { apiClient } from '../utils/apiClient';

/**
 * REST surface for chat: conversation lifecycle, message history pagination,
 * membership, the read cursor, the global user directory, and attachment
 * upload. Live message send/typing/read go over the WS channel
 * (chatChannelService); everything server-owned or paginated lives here.
 */
class ChatService {
  async listChannels() {
    const res = await apiClient.get('/chat/channels');
    return res.data.channels; // [channel]
  }

  async createChannel({ kind, name = null, memberIds = [], color = null }) {
    const res = await apiClient.post('/chat/channels', { kind, name, member_ids: memberIds, color });
    return res.data.channel;
  }

  /**
   * Upload a channel avatar image (png/jpeg/webp/gif). Mirrors the IdP
   * avatar-upload shape (services/idpAccountService#uploadAvatar). Returns the
   * updated channel (with `avatar_url`). Backend endpoint may not exist yet —
   * callers should treat a 404/failure as "avatar unsupported" and keep the
   * color-only channel.
   */
  async uploadChannelAvatar(channelId, file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post(`/chat/channels/${channelId}/avatar`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.channel;
  }

  async createDm(userId) {
    const res = await apiClient.post('/chat/dms', { user_id: userId });
    return res.data.channel;
  }

  /**
   * REST message send — works for any conversation the user is a member of,
   * unlike the WS path which only reaches the currently joined topic. The
   * server does the same fan-out as a WS send (chat:{id} + user:{id} topics),
   * so open viewers and unread badges update either way. Idempotent on uid.
   */
  async sendMessage(channelId, { uid, body = '', kind = 'text', metadata = {} }) {
    const res = await apiClient.post(`/chat/channels/${channelId}/messages`, {
      uid,
      body,
      kind,
      metadata,
    });
    return res.data.message;
  }

  async getMessages(channelId, { beforeSeq = null, limit = null } = {}) {
    const params = {};
    if (beforeSeq != null) params.before_seq = beforeSeq;
    if (limit != null) params.limit = limit;
    const res = await apiClient.get(`/chat/channels/${channelId}/messages`, { params });
    const messages = res.data.messages; // ascending by server_seq
    // Plans with a chat history window (Free: 90 days) get a page the SERVER
    // truncated. The flag rides on the array so every existing caller is
    // unchanged, and so `pageDeletionSpan` can tell "the server is hiding older
    // messages from this plan" from "those messages were unsent" — without it,
    // an empty tip page on an old conversation deletes the device's whole cache
    // of a history the server still holds.
    if (Array.isArray(messages) && res.data.history_days != null) {
      Object.defineProperty(messages, 'historyDays', { value: res.data.history_days, enumerable: false });
    }
    return messages;
  }

  /**
   * Search one conversation (GET /chat/channels/:id/search, PLAN §5.1).
   * `params`: { q?, has?[], from?[], before?, after?, sort?, cursor?, limit? }.
   * The search text MUST travel as `q` — the server scrubs that name from its
   * logs and Sentry. Arrays go out as `has[]=a&has[]=b` (axios' default
   * serializer; apiClient sets no paramsSerializer). Returns the wire body
   * as is: { messages, next_cursor, complete, total, total_capped, scanned,
   * history_days }.
   */
  async searchMessages(channelId, params, { signal } = {}) {
    const res = await apiClient.get(`/chat/channels/${channelId}/search`, { params, signal });
    return res.data;
  }

  /**
   * One page of the plan-gated history export (GET /chat/channels/:id/export,
   * PLAN §5.2): ascending, secrets and share tokens already redacted by the
   * server. 402 { upgrade_required } for Free.
   */
  async exportPage(channelId, { afterSeq = 0, limit = 500 } = {}, { signal } = {}) {
    const res = await apiClient.get(`/chat/channels/${channelId}/export`, {
      params: { after_seq: afterSeq, limit },
      signal,
    });
    return {
      messages: Array.isArray(res.data?.messages) ? res.data.messages : [],
      nextAfterSeq: res.data?.next_after_seq ?? null,
      historyDays: res.data?.history_days ?? null,
    };
  }

  async addMember(channelId, userId) {
    const res = await apiClient.post(`/chat/channels/${channelId}/members`, { user_id: userId });
    return res.data.channel;
  }

  /**
   * Delete an ORPHANED direct message and every file uploaded to it. The
   * server enforces eligibility (dm, caller is a member, caller is the only
   * member left) — this is irreversible, so the check that matters is the one
   * the client cannot skip.
   */
  async deleteChannel(channelId) {
    const response = await apiClient.delete(`/chat/channels/${channelId}`);
    return response.data;
  }

  async removeMember(channelId, userId) {
    const res = await apiClient.delete(`/chat/channels/${channelId}/members/${userId}`);
    return res.data;
  }

  async markRead(channelId, seq) {
    await apiClient.post(`/chat/channels/${channelId}/read`, { seq });
  }

  /**
   * Per-viewer mute. Server-side (not a local preference) so muting on one
   * device silences the conversation on every device the account is signed
   * into. Returns the re-serialized channel, whose `muted` boolean is the
   * viewer's own — see `utils/chatAlerts.js` for what mute actually suppresses.
   */
  async setMuted(channelId, muted) {
    const res = await apiClient.put(`/chat/channels/${channelId}/mute`, { muted });
    return res.data?.channel ?? null;
  }

  /**
   * People lookup (GET /chat/users/search, PLAN §12.3.1): a complete e-mail
   * finds anyone (exact); any other text of 2..100 characters finds RELATED
   * users by name (shared team, workspace or channel). `limit` 1..25 (the
   * server ignores it on the e-mail path); `signal` aborts a typeahead call.
   * An older server answers `[]` for a name — the caller treats that as "no
   * server rows".
   */
  async searchUsers(q, { limit, signal } = {}) {
    const params = { q };
    if (limit != null) params.limit = limit;
    const res = await apiClient.get('/chat/users/search', { params, signal });
    return res.data?.users || []; // [{ id, name, email, relation }]
  }

  /**
   * Upload an attachment blob. Accepts a prebuilt FormData (encrypted flow:
   * ciphertext `file` + declared plaintext `name`/`mime`/`size` fields) or a
   * bare File for backwards compatibility.
   */
  async uploadAttachment(channelId, fileOrForm) {
    let form = fileOrForm;
    if (!(form instanceof FormData)) {
      form = new FormData();
      form.append('file', fileOrForm);
    }
    const res = await apiClient.post(`/chat/channels/${channelId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data; // { url, name, size, mime, kind, enc }
  }

  /**
   * The channel's attachment-encryption key (base64), member-gated. Fallback
   * to the WS join reply — lets history images decrypt without a live socket.
   */
  async getChannelKey(channelId) {
    const res = await apiClient.get(`/chat/channels/${channelId}/key`);
    return res.data.key;
  }

  async acceptInvitation(token) {
    const res = await apiClient.post('/chat/invitations/accept', { token });
    return res.data.channel;
  }

  /**
   * Server-side chat privacy preferences. Read receipts are enforced by the
   * backend (reciprocal, WhatsApp-style): when off, others don't see your
   * reads and you don't see theirs.
   */
  async getChatPreferences() {
    const res = await apiClient.get('/chat/preferences');
    return res.data; // { read_receipts }
  }

  async updateChatPreferences({ readReceipts }) {
    const res = await apiClient.put('/chat/preferences', { read_receipts: readReceipts });
    return res.data;
  }
}

const chatService = new ChatService();
export default chatService;
